import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Award } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
    <span className="line-mask">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.05, delay, ease }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yImg = useTransform(scrollYProgress, [0, 1], [0, 160]);
    const rotate = useTransform(scrollYProgress, [0, 1], [0, 12]);
    const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section
            id="hero"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-screen flex flex-col justify-center pt-28 pb-16 px-6 md:px-10 max-w-[1400px] mx-auto"
        >
            <motion.div
                aria-hidden="true"
                style={{ y: yImg, rotate }}
                className="pointer-events-none hidden lg:block absolute right-2 md:right-10 top-[16%] w-56 h-56 xl:w-64 xl:h-64 z-0"
            >
                <div className="w-full h-full rounded-full overflow-hidden ring-1 ring-black/10">
                    <img
                        src="https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2, ease }}
                className="relative z-20 mb-8"
            >
                <div
                    data-testid="uk-badge"
                    className="inline-flex items-center gap-3 bg-[#11141D] border border-[#C5A059]/40 text-[#FDFBF7] rounded-sm pl-2.5 pr-5 py-2.5 shadow-[0_8px_30px_rgba(0,0,0,0.35)] max-w-[92vw]"
                >
                    <span className="w-8 h-8 rounded-full bg-[#C5A059] flex items-center justify-center text-[#070A13] shrink-0">
                        <Award size={16} aria-hidden="true" />
                    </span>
                    <span className="flex flex-col leading-tight">
                        <span className="overline text-[#C5A059]">
                            UK&apos;s first
                        </span>
                        <span className="font-mono text-[11px] tracking-wide text-white/55 mt-1">
                            Raw math for people and their businesses
                        </span>
                    </span>
                </div>
            </motion.div>

            <motion.h1
                style={{ y: yText }}
                className="font-display font-light tracking-tight leading-[0.92] text-7xl md:text-8xl lg:text-[10rem] relative z-10"
            >
                <Line delay={0.35}>THE NUMBERS</Line>
                <Line delay={0.5}>
                    <span className="italic font-medium text-[#C5A059]">
                        don&apos;t
                    </span>{" "}
                    lie.
                </Line>
            </motion.h1>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.1, ease }}
                className="mt-12 grid md:grid-cols-12 gap-8 items-end"
            >
                <p className="md:col-span-6 font-body text-lg md:text-xl text-[#8E95A3] leading-relaxed max-w-xl">
                    I read your real numbers and tell you the truth: where your
                    money actually goes, what&apos;s quietly holding you back,
                    and the fastest route to what you want — the move, the home,
                    the clarity to choose. No jargon, no judgement, just a clear
                    plan you can act on this week.
                </p>
                <div className="md:col-span-6 md:col-start-9 flex flex-col md:items-end gap-3">
                    <button
                        data-testid="hero-cta"
                        onClick={() => scrollToId("#pricing")}
                        className="group inline-flex items-center gap-3 font-body font-semibold text-lg"
                    >
                        <span className="relative">
                            Book my Signal call
                            <span className="absolute -bottom-1 left-0 w-full h-px bg-[#C5A059] origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-300" />
                            <span className="absolute -bottom-1 left-0 w-full h-px bg-[#C5A059] origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-300 delay-100" />
                        </span>
                        <span className="w-11 h-11 rounded-full bg-[#C5A059] text-[#070A13] flex items-center justify-center transition-transform duration-300 group-hover:bg-[#C5A059] group-hover:rotate-45">
                            <ArrowDownRight size={18} />
                        </span>
                    </button>
                    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#C5A059] md:text-right">
                        Launch price — first 10 calls only
                    </p>
                </div>
            </motion.div>
        </section>
    );
};
